import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Star } from "lucide-react";
import api from "@/lib/api";
import { SectionHeading, LuxButton, revealProps, Eyebrow } from "@/components/lux";

function PackageCard({ pkg, index }) {
  const navigate = useNavigate();
  return (
    <motion.div
      {...revealProps(index * 0.12)}
      whileHover={{ y: -10 }}
      className={`relative flex flex-col rounded-2xl p-7 sm:p-8 border transition-all duration-500 ${
        pkg.featured
          ? "bg-gradient-to-b from-[#0d2730] to-[#12141C] border-[#02AACF]/60 brand-glow"
          : "bg-[#12141C] border-white/10 hover:border-white/25"
      }`}
      data-testid={`package-card-${pkg.slug}`}
    >
      {pkg.featured && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-[#02AACF] text-white text-[10px] font-mono2 tracking-[0.2em] uppercase px-4 py-1.5 rounded-full">
          <Star size={11} fill="white" /> Recommended
        </div>
      )}
      <Eyebrow>{pkg.short_name}</Eyebrow>
      <h3 className="font-display font-bold text-2xl text-white mt-2">{pkg.name}</h3>
      <p className="text-[#94A3B8] text-sm mt-2 min-h-[40px]">{pkg.tagline}</p>

      <div className="flex items-end gap-1 mt-5">
        {pkg.price ? (
          <>
            {pkg.starting_price && <span className="text-[#64748B] text-xs mb-1.5 font-mono2 mr-1">From</span>}
            <span className="font-display font-bold text-4xl text-white">{pkg.currency}{pkg.price}</span>
            {pkg.deposit ? <span className="text-[#64748B] text-xs mb-1.5 font-mono2">/ {pkg.currency}{pkg.deposit} deposit</span> : null}
          </>
        ) : (
          <span className="font-display font-bold text-xl text-[#02AACF]">Contact for pricing</span>
        )}
      </div>

      <div className="h-px bg-white/10 my-6" />

      <ul className="space-y-3 flex-1">
        {(pkg.features || []).map((f, i) => (
          <li key={i} className="flex gap-3 text-sm text-[#cbd5e1]">
            <Check size={16} className="text-[#02AACF] shrink-0 mt-0.5" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {pkg.fine_print && <p className="text-[11px] text-[#64748B] mt-5 leading-relaxed italic">{pkg.fine_print}</p>}

      <LuxButton
        className="w-full mt-7"
        variant={pkg.featured ? "primary" : "outline"}
        data-testid={`book-${pkg.slug}`}
        onClick={() => navigate(`/book?package=${pkg.slug}`)}
      >
        BOOK {pkg.short_name.toUpperCase()}
      </LuxButton>
    </motion.div>
  );
}

export default function PackagesSection() {
  const [packages, setPackages] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, []);

  return (
    <section id="packages" className="relative py-24 sm:py-32 bg-[#0b0d12] grain" data-testid="packages-section">
      <div className="mx-auto max-w-[1300px] px-5 sm:px-8">
        <motion.div {...revealProps()} className="text-center max-w-2xl mx-auto">
          <SectionHeading align="center" eyebrow="Investment" title="Photography Packages"
            subtitle="Transparent pricing for every kind of shoot. Every package is fully tailored on the day." />
        </motion.div>

        <div className="mt-14 grid md:grid-cols-3 gap-6 items-stretch">
          {packages.map((p, i) => <PackageCard key={p.id} pkg={p} index={i} />)}
        </div>

        <motion.div {...revealProps(0.2)} className="mt-10 rounded-2xl border border-dashed border-white/15 bg-[#12141C] p-8 sm:p-10 flex flex-col sm:flex-row items-center justify-between gap-6" data-testid="custom-quote-cta">
          <div>
            <Eyebrow>Need Something Different?</Eyebrow>
            <h3 className="font-display font-bold text-2xl text-white mt-2">Request a Custom Quote</h3>
            <p className="text-[#94A3B8] mt-2 max-w-xl text-sm">For events, campaigns, corporate & government work, product launches, travel photography and anything outside the standard packages.</p>
          </div>
          <LuxButton size="lg" variant="dark" className="shrink-0" data-testid="request-custom-quote" onClick={() => navigate("/book?type=quote")}>
            REQUEST A QUOTE
          </LuxButton>
        </motion.div>
      </div>
    </section>
  );
}
